import Backbone from "backbone";
import React from "react";
import { createRoot, type Root } from "react-dom/client";
import { ThemeDrive, LanguageProvider, UserFormatProvider } from "@geotab/zenith";
import type { TSupportedLanguage } from "@geotab/zenith/dist/utils/localization/getSupportedLanguage";
import rison from "rison";
import _ from "underscore";
import DriveView from "../../../../DriveView";
import GraphView from "../../graph";
import HosNavigateDateMenu from "../../hosNavigation";
import * as logger from "../../../../utils/logger";
import HosSubMenu from "./../../subnavigation";
import { dispatcher, DispatcherEvents } from "../../../../utils/dispatcher";
import { generateChecklist, ListView } from "../../../framework/checklist";
import { Dialog } from "../../../framework/dialog";
import { INewAnnotation, IDailyOdometer } from "../../../../model/DutyStatusLogs";
import { convertMinutesToTimeString } from "../../../../utils/helpers";
import {
    convertDecimalToTimeString, defPhrases, formatUtcDateString, type IDutyStatusLog, isInCanada,
    populateViolationReason, setCoDriversForCarrierDriverRequest, type IViolation
} from "hos-tools";
import { getCurrentVehicle } from "../../../../utils/device";
import { dstCalculator, getLocalDaySpan, getStartOfDayUserTimezone, getDateTokenString, makeHistoricalDayKey, IReportPeriod } from "../../../../utils/date";
import Sync from "../../../../sync/sync";
import Api from "../../../../services/api";
import HosProcess from "../../../../services/hosProcess";
import DutyStatusLog, { IAddress, IAnnotation } from "../../../../model/DutyStatusLog";
import { htmlEscape, extend, createElement, hide, show, isEqual } from "../../../../utils/vanilla";
import { CancelablePromise } from "../../../../utils/cancelablePromise";
import { getServerDate } from "../../../../utils/storage/date";
import { IAddressSearch, populateAddressesRedux as fetchAddresses, populateDownloadedAddressesRedux } from "../../../../sync/common";
import dayjs from "../../../../utils/dayjs";
import toastr from "../../../../utils/toastr";
import constants from "../../../../constants";
import { SyncType } from "../../../../sync/sync.types";
import { AuditType, auditSkip } from "../../../../utils/auditHelper";
import { IViewedLog, NetworkStates, PageLevel } from "../../../../store/mobile/types";
import { MobileActions } from "../../../../store/mobile/actions";
import { HistoricalDayActions } from "../../../../store/hosLogs/actions";
import Spinner, { BigSpinner } from "../../../../utils/spinner";
import { HistoricalDayLoader } from "../../../../services/historicalDayLoader";
import idSerializer from "geotab-uuid";
import { ExpandableList } from "../../../framework/expandableList/expandableList";
import { ExpandableItem } from "../../../framework/expandableList/ExpandableItem";
import { getCustomExemptHOSAbbreviation, getOdometer, addLogDistance, getTranslatedStatus } from "../../../../utils/hosHelpers";
import { ICachedAddress } from "../../../../store/data/types";
import { handleApi, handleJSNative } from "../../../../utils/error";
import logsTemplate from "./logs.regular.tmpl";
import logTemplate from "./log.tmpl";
import logHeaderTemplate from "./log.header.tmpl";
import odometerItemTemplate from "./logs.odometer.tmpl";
import { DateSelector } from "../../../framework/dateSelector/dateSelector";
import { HosAddress } from "../../../framework/hosAddress/hosAddress";
import PromiseDebounce from "../../../../utils/promiseDebounce";
import { getClonedAnnotationsForUser } from "../../../../utils/storeHelpers";
import { OpenCabWorkflowActions } from "../../../../store/openCabWorkflow/actions";
import { FeatureFlagName } from "../../../../utils/featureFlagName";
import certifyDialogTemplate from "./certifyDialog.tmpl";
import { formatExemptionEditAnnotation, getLinkedExemptionAnnotations, indexLinkedExemptionAnnotations } from "./exemptionEditAnnotations";

interface IAddressCache {
    [logId: string]: IAddress;
}

/* eslint-disable @typescript-eslint/naming-convention */
export interface IViewLog extends DutyStatusLog {
    className?: string;
    _annotations?: string[];
    _isControlLog?: boolean;
    _controlLogBadge?: string;
    _hasAddress?: boolean;
    _violations?: any[];
    _date?: string;
    _odometer?: string;
    _isVerified?: boolean;
    _isTransitioning?: boolean;
    _isPlaceholder?: boolean;
    _isRequestedEdit?: boolean;
    _duration?: string;
    _translatedStatus?: string;
    _customExemptHOSAbbreviation?: string;
    _isEWD?: boolean;
    _isLoginLogoffLog?: boolean;
    hosAddressHtml?: string;
}

interface IDisplayViolation extends IViolation {
    _fromDate: string;
}
/* eslint-enable @typescript-eslint/naming-convention */

interface IDailyHeaderData {
    dateTime: Date;
    mileageDate: string;
    mileageTotal: string;
    onDutyTime: string;
    isAllDayVerified: boolean;
    isRequestedLogsDay: boolean;
    verifyOnly: boolean;
    dailyViolations: string[];
    isInCanadaWithPC?: boolean;
    isEditedPlaceholderDay: boolean;
    isEWD: boolean;
    ignoredLogMessage: string;
    verifyLabel: string;
    verifyDateLabel: string;
}

interface IDailyView {
    dailyHeaderData: IDailyHeaderData;
    dailyLogs: IViewLog[];
}

interface IDailyViewDictionary {
    [dateHash: string]: IDailyView;
}

const ON_DUTY_LOGS = ["ON", "D", "YM", "Work", "WorkExemption"];

export default class LogsView extends DriveView<Backbone.Model> {

    // Translate strings used for logs list in advance to prevent duplicate translations
    protected strings = [
        "The entered address could not be found. Please enter a valid address:",
        " Certify ",
        "Enter location",
        "h",
        "{minutes}m",
        "Excludes {0} ignored logs ({time}) which will still appear on Roadside Checks.",
        "Certify",
        "Accept and certify",
        "OFF",
        "SB",
        "D",
        "ON",
        "PC",
        "YM",
        "WT"
    ].concat(Object.keys(defPhrases).map(type => defPhrases[type]["longString"]));
    private readonly sync: Sync;
    private readonly api: Api;
    private readonly hosUtils: HosProcess;
    private readonly clickLogAction: any;

    private readonly loggingOut: boolean;
    private readonly loggingIn: boolean;
    private readonly verifyOnly: boolean;

    private readonly templateDailyHeader = _.template(logHeaderTemplate);
    private readonly templateLogs = _.template(logsTemplate);
    private readonly odometerItemTemplate = _.template(odometerItemTemplate);
    private readonly spinner = new Spinner({
        top: "auto",
        left: "auto"
    });
    private readonly bigSpinner = new BigSpinner();
    private readonly historicalDayLoader: HistoricalDayLoader;

    private readonly menu: HosSubMenu;
    private readonly hosAddress: HosAddress;
    private readonly isEWD: boolean;
    private readonly syncCompletePromiseDebounce: PromiseDebounce;
    private readonly isEmergencyResponseExemptionEnabled: boolean;
    private graphView: GraphView | undefined;
    private navigationView: HosNavigateDateMenu | undefined;
    private dateSelectorReactRoot: Root | null = null;
    private dateSelectorContainer: HTMLElement | null = null;
    private renderChecklistTimeout;

    private clickedLog?: IViewedLog;
    private currentDay: Date;
    private dailyLogViews: IDailyViewDictionary = {};
    private logListViews: ListView[] = [];
    private logsCollection: DutyStatusLog[] = [];
    private filteredCollection: DutyStatusLog[];
    private logsAddressCache: IAddressCache;
    private annotations;
    private violations: IViolation[];
    private populateLogsPromise: CancelablePromise<void>;
    private historicalLoadPromise: CancelablePromise<void> | undefined;
    private historicalLogs: DutyStatusLog[] | null = null;
    private historicalViolations: IViolation[] | null = null;
    // Per-device odometer start/end bounds for the currently-loaded historical day (from the
    // historicalDayTracker cache). Passed to getOdometer for historical days; null in-window.
    private historicalOdometer: IDailyOdometer | null = null;
    private isHistoricalLoading = false;
    private historicalLoadError: string | null = null;
    private historicalLoadDebounceTimer: ReturnType<typeof setTimeout> | undefined;
    private requestedAddShortenYMPCLogIdSet: Set<string> = new Set();
    constructor(options) {
        super(options, {
            title: options && (options.loggingOut || options.loggingIn) ? "Certify Logs" : "HOS",
            activeSection: "hos",
            activeView: "logs",
            pageLevel: options && options.pageLevel || PageLevel.ONE
        });

        this.app = options.app;
        this.isEWD = this.app.store.getState().data.ELDJurisdiction === "EWD";
        this.sync = this.app.sync;
        this.api = this.app.api;
        this.hosUtils = this.app.hosUtils;
        this.model = new Backbone.Model();
        this.isEmergencyResponseExemptionEnabled = this.app.featureFlagService.hasFeatureFlag(FeatureFlagName.ENABLE_EMERGENCY_RESPONSE_EXEMPTION);
        this.historicalDayLoader = new HistoricalDayLoader(this.api, this.app.dutyStatusLogService, this.app.store);
        this.clickLogAction = options && options.clickLogAction;
        this.loggingOut = options && options.loggingOut;
        this.loggingIn = options && options.loggingIn;
        this.verifyOnly = this.loggingOut || this.loggingIn;
        this.clickedLog = this.app.store.getState().mobile.lastViewedLog;
        this.hosAddress = new HosAddress(this.translator, this.sync);

        if (!this.verifyOnly) {
            this.isScrollBtnEnabled = true;

            this.menu = new HosSubMenu({ app: this.app, active: "logs" });
            this.graphView = new GraphView({ app: this.app });

            this.navigationView = new HosNavigateDateMenu({
                numDays: this.hosUtils.getAmountOfDays(this.app.userService.getCurrentUser(), 2),
                app: this.app,
                callback: this.navigateToDate
            });
        }

        // HosRuleCompute is triggered by SyncHos. We need the up-to-date violations to render the logs list.
        // Only do a full render if it's after a sync
        this.listenTo(dispatcher, DispatcherEvents.HosRuleCompute, (_1, ruleResult) => {
            if (Object.keys(this.dailyLogViews).length > 0
                && ruleResult && ruleResult.violations
                && !isEqual(this.violations, ruleResult.violations, ["drivingDuration"])) {
                // Only do this if it's been rendered once already and violations changed
                this.populateViolations(this.dailyLogViews);
                void this.renderTemplate();
            }
        });

        this.listenTo(dispatcher, DispatcherEvents.SyncHosLocationInvalid, (address) => {
            toastr.warning(
                `${ this.getString("The entered address could not be found. Please enter a valid address:") } ${ htmlEscape(address) }`,
                undefined,
                undefined,
                { isUserNightMode: this.app.store.getState().data.nightMode }
            );
        });
        this.syncCompletePromiseDebounce = new PromiseDebounce(msg => logger.log(logger.Source.UI, msg), msg => logger.error(logger.Source.UI, msg), handleJSNative);
        this.listenTo(dispatcher, DispatcherEvents.SyncComplete, () => {
            if (!this.filteredCollection) {
                return;
            }
            // Using the promise debouncer will ensure that this function is not called
            // more often than needed, especially when adding many logs in quick succession
            this.syncCompletePromiseDebounce.run((resolve, reject) => {
                this.loadLogs();
                return this.populateLogs().then(() => resolve()).catch(() => reject());
            });
        });

        this.loadLogs();
        if (this.clickedLog) {
            this.clearClickedLog();

            if (!this.verifyOnly) {
                if (this.clickedLog.id) {
                    let logFromStorage = this.app.dutyStatusLogService.getLogById(this.logsCollection, this.clickedLog.id) as DutyStatusLog;
                    if (logFromStorage) {
                        this.currentDay = getStartOfDayUserTimezone(logFromStorage.getDate());
                        return;
                    }
                }

                if (this.clickedLog.date) {
                    this.currentDay = new Date(this.clickedLog.date);
                    return;
                }
            }
        }
        this.currentDay = getStartOfDayUserTimezone(getServerDate());
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click #addLog": this.addLog,
            "click #malfunctions": this.seeMalfunctions,
            "click .addressButton": this.manualInputAddress,
            "click .verifySave": this.verify,
            "click .verifyAndAcceptDay": this.verifyAndAcceptDay,
            "click #acceptAndVerifyAll": this.acceptAndVerifyAll,
            "click #verifyAll": this.verifyAll,
            "click #verifySkip": this.proceedWithAudit,
            "click #verifyEditedPlaceholder": this.goToRealLog,
            "click #hos-historical-reload": this.retryHistoricalLoad
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    render() {
        const store = this.app.store;
        const pageState = store.getState().mobile.pageState;
        if (pageState?.title === "HOS" && store.getState().data.ELDJurisdiction === "EWD") {
            store.dispatch(MobileActions.setPageState({
                ...pageState,
                ...{ title: "Work Diary" }
            }));
        }
        this.app.localizerService.initUserUnits(this.translator)
            .then(() => this.populateLogs(true))
            .catch(handleApi);
        return this;
    }

    remove() {
        this.app.setFullWidth(false);
        clearTimeout(this.renderChecklistTimeout);
        this.cancelPendingPromises();
        this.syncCompletePromiseDebounce.reset();
        this.removeListViews();
        this.dailyLogViews = {};
        this.spinner.stop();
        this.bigSpinner.stop();

        this.menu && this.menu.remove();

        this.graphView && this.graphView.remove();
        this.graphView = undefined;
        if (this.dateSelectorReactRoot) {
            this.dateSelectorReactRoot.unmount();
            this.dateSelectorReactRoot = null;
            this.dateSelectorContainer = null;
        }
        this.navigationView?.remove();
        this.navigationView = undefined;

        clearTimeout(this.historicalLoadDebounceTimer);
        this.historicalLoadDebounceTimer = undefined;
        this.isHistoricalLoading = false;
        this.historicalLoadPromise?.cancel();
        this.historicalLoadPromise = undefined;

        super.remove();
        return this;
    }

    private updateViewDates() {
        this.navigationView?.updateDate(this.currentDay);
        this.graphView?.updateDisplayedDate(this.currentDay);
    }

    private readonly navigateToDate = (newDate: Date) => {
        this.clickedLog = undefined;
        this.currentDay = newDate;
        this.historicalLogs = null;
        this.historicalViolations = null;
        this.historicalOdometer = null;
        this.historicalLoadError = null;
        this.updateViewDates();

        clearTimeout(this.historicalLoadDebounceTimer);
        this.historicalLoadDebounceTimer = undefined;
        this.isHistoricalLoading = false;

        if (this.shouldUseHistoricalLoading(newDate)) {
            // Adoption signal: user navigated (chevron or date selector) to a historical day.
            logger.event(
                logger.Event.HISTORICALDAYADOPTION,
                JSON.stringify({ source: "navigation", dateKey: this.makeDateKey(newDate) }),
                this.app.userService.getCurrentUser()
            );
            // Show the loading spinner during the debounce window. The date navigation stays
            // rendered (see renderTemplateView) so the user can keep navigating before a load fires.
            this.isHistoricalLoading = true;
            void this.renderTemplate();
            this.historicalLoadDebounceTimer = setTimeout(() => {
                this.historicalLoadDebounceTimer = undefined;
                this.isHistoricalLoading = false;
                void this.populateLogs();
            }, 1000);
        } else {
            this.graphView?.loadLogs();
            void this.populateLogs();
        }
    };

    private loadLogs() {
        const currentUser = this.app.userService.getCurrentUser();

        this.logsCollection = this.app.dutyStatusLogService.getSortedHosLogs(this.app.dutyStatusLogService.getHOSLogs(false)
            .filter(hosLog => hosLog.isMainLogPageLog(currentUser, undefined, this.isEWD, this.isEmergencyResponseExemptionEnabled)
                && (!currentUser || !this.isRejectedTransition(hosLog))));

        const [requestedAddShortenYMPCLogIdSet, filteredLogsCollection] = this.app.dutyStatusLogService.findModifiedExemptionLogsAndFilterRequestedAdds(this.logsCollection);
        this.requestedAddShortenYMPCLogIdSet = requestedAddShortenYMPCLogIdSet;
        this.logsCollection = filteredLogsCollection;

        this.navigationView?.loadLogs(this.logsCollection, this.requestedAddShortenYMPCLogIdSet);
        this.graphView?.loadLogs();

        const apiUserId = this.api.getUser()?.id;
        this.annotations = getClonedAnnotationsForUser(this.app.store, apiUserId);

        this.logsAddressCache = this.logsAddressCache || {};
    }

    private isRejectedTransition(hosLog: DutyStatusLog) {
        return (hosLog.requestedEdit?.state === "Rejected" && hosLog.driver.id !== this.app.userService.getCurrentUser()?.id) ||
            (!hosLog.requestedEdit && hosLog.driver.id !== this.app.userService.getCurrentUser()?.id);
    }

    private getDailyViolations(date: Date, violations: any[], translate: (s: string) => string): string[] {
        return violations.reduce((res: any[], violation: IViolation) => {
            let violationDate = getStartOfDayUserTimezone(violation.fromDate).getTime();

            // For now just get dailyRest and dailyOff violations
            if (violationDate === date.getTime() && (violation.type === "DailyRest" || violation.type === "DailyOff")) {
                res.push(populateViolationReason(violation, translate));
            }
            return res;
        }, []);
    }

    private populateViolations(dailyLogsDict: IDailyViewDictionary = {}) {
        // For historical days outside the sync window, use violations fetched from the API.
        // Otherwise use the local rule processor result.
        if (this.historicalViolations !== null) {
            this.violations = this.historicalViolations;
        } else {
            let ruleResult = this.hosUtils.getRuleResult();
            this.violations = ruleResult ? ruleResult.violations : [];
        }
        let currentDate = getServerDate();

        // Translate the violation strings
        let translateString = (englishString) => this.getString(englishString);
        let violationObjs = this.violations.map(violation => {
            let fromDate = dayjs(dstCalculator.toLocalDateTime(violation.fromDate > currentDate ? currentDate : violation.fromDate)).format("LT");
            return extend({}, violation, <IDisplayViolation>{ _fromDate: fromDate, reason: populateViolationReason(violation, translateString) });
        });

        Object.keys(dailyLogsDict).forEach(dailyKey => {
            let dailyViolations = this.getDailyViolations(new Date(dailyKey), violationObjs, translateString);
            dailyLogsDict[dailyKey].dailyHeaderData.dailyViolations = dailyViolations;

            // Update the logs in the log collection with the translated violation strings
            dailyLogsDict[dailyKey].dailyLogs.forEach((log: any) => {
                let logViolations = violationObjs.filter(violation => violation.logId === log.id);
                log._violations = logViolations;
            });
        });
    }

    private getLogsForView() {
        if (this.verifyOnly) {
            return this.logsCollection;
        }

        if (this.shouldUseHistoricalLoading(this.currentDay)) {
            this.updateViewDates();
            return this.getHistoricalLogsForView();
        }

        const daySpan = getLocalDaySpan(this.currentDay);
        const logCollection = this.app.dutyStatusLogService.getLogsByDate(this.logsCollection, daySpan);

        this.updateViewDates();
        return logCollection;
    }

    private cancelPendingPromises() {
        this.populateLogsPromise && this.populateLogsPromise.cancel();
        this.historicalLoadPromise && this.historicalLoadPromise.cancel();
    }

    private isOutsideSyncWindow(day: Date): boolean {
        return day < this.hosUtils.getRetentionStartDate(2);
    }

    private getHistoricalLogsForView(): DutyStatusLog[] {
        return (this.historicalLogs ?? []).filter(log => this.isEmergencyResponseExemptionEnabled || !log.isEmergencyResponseExemptionLog());
    }

    private shouldUseHistoricalLoading(day: Date): boolean {
        return this.isOutsideSyncWindow(day);
    }

    /**
     * Odometer source for the currently-viewed day. Historical days read their cached fragment (kept
     * out of the shared dailyOdometer store, which sync replaces wholesale); in-window days return
     * undefined so getOdometer falls back to the shared store.
     */
    private getHistoricalOdometerSource(): IDailyOdometer | undefined {
        return this.shouldUseHistoricalLoading(this.currentDay) ? (this.historicalOdometer ?? undefined) : undefined;
    }

    /**
     * Removes every cached historical day for the user except `keepDateKey`. Used after a requested
     * edit is accepted so adjacent/forward days re-fetch their boundary continuation instead of
     * serving a stale "requested" value from the cache.
     */
    private invalidateOtherCachedDays(userId: string, keepDateKey: string): void {
        const trackedDays = this.app.store.getState().historicalDayTracker[userId];
        if (!trackedDays) {
            return;
        }
        Object.keys(trackedDays).filter(dateKey => dateKey !== keepDateKey).forEach(dateKey => {
            this.app.store.dispatch(HistoricalDayActions.untrackHistoricalDay({ userId, dateKey }));
        });
    }

    private makeDateKey(day: Date): string {
        return makeHistoricalDayKey(day);
    }

    private loadHistoricalDayData(day: Date): CancelablePromise<void> {
        this.historicalLoadPromise && this.historicalLoadPromise.cancel();
        this.isHistoricalLoading = true;
        this.historicalLoadError = null;
        void this.renderTemplate();

        const userId = this.api.getUser()?.id;
        if (!userId) {
            this.isHistoricalLoading = false;
            this.historicalLoadError = "Unable to load logs. Please try again.";
            void this.renderTemplate();
            return CancelablePromise.resolve();
        }

        const daySpan = getLocalDaySpan(day);
        const dateKey = this.makeDateKey(day);
        const regulationsFromDate = this.hosUtils.getRetentionStartDate(1, day);

        this.historicalLoadPromise = this.historicalDayLoader.loadHistoricalDayData(
            daySpan.fromDate,
            daySpan.toDate,
            regulationsFromDate,
            userId,
            dateKey
        ).then(result => {
            this.isHistoricalLoading = false;
            if (this.currentDay !== day) {
                return; // navigated away while fetch was in-flight — discard stale result
            }

            this.historicalLogs = result.userHosLogs;
            this.historicalViolations = result.violations;
            this.historicalOdometer = result.odometer ?? null;
            this.graphView?.loadLogs([...result.userHosLogs, ...result.systemHosLogs]);
            this.graphView?.updateDisplayedDate(this.currentDay);
        }).catch(err => {
            logger.error(logger.Source.ERROR, `Failed to load historical day: ${ err?.message ?? "" }`, this.app.userService.getCurrentUser(), err);
            this.isHistoricalLoading = false;
            this.historicalLoadError = err?.message === NetworkStates.OFFLINE
                ? "No internet connection. Unable to load historical logs."
                : "Unable to load logs. Please try again.";
            void this.renderTemplate();
        });

        return this.historicalLoadPromise;
    }

    private retryHistoricalLoad() {
        // Go through populateLogs (not loadHistoricalDayData directly) so the logs render and
        // the spinner is cleared on success — loadHistoricalDayData relies on its caller to render.
        void this.populateLogs();
    }

    private readonly populateLogs = async (forceRender = false) => {
        // Don't forget to update WorkflowView.checkIfUnverifiedLogs if you modify this lines!

        const dayAtCallStart = this.currentDay;
        let isHistoricalDay = this.shouldUseHistoricalLoading(this.currentDay);
        let filteredCollection: DutyStatusLog[] = [];
        if (isHistoricalDay && !this.isHistoricalLoading) {
            await this.loadHistoricalDayData(this.currentDay);
            if (this.historicalLoadError || this.currentDay !== dayAtCallStart) {
                return;
            }
            this.loadLogs();
            filteredCollection = this.getHistoricalLogsForView();
        } else {
            filteredCollection = this.getLogsForView();
        }

        let dateRange: IDateRange = {
            startDate: this.historicalLogs !== null && isHistoricalDay
                ? getStartOfDayUserTimezone(this.currentDay)
                : this.hosUtils.getRetentionStartDate(2),
            endDate: this.loggingIn ? getStartOfDayUserTimezone(getServerDate()) : constants.MAX_DATE
        };
        let isDateExcluded = log => {
            let date = log.getDate();
            return (date < dateRange.startDate) || (dateRange.endDate && date.getTime() >= dateRange.endDate.getTime());
        };

        let unverifiedDays = this.app.dutyStatusLogService.getUnverifiedLocalDays(filteredCollection, dateRange, this.requestedAddShortenYMPCLogIdSet);

        // If we're on the verifyPage, don't display logs that don't have an ID
        // If we're on the regular logs page, don't display verified logs older than the reporting start date
        let logFilter: (a: DutyStatusLog) => boolean;
        if (this.verifyOnly) {
            logFilter = log => !unverifiedDays[getDateTokenString(log.getDate(), true)]
                || isDateExcluded(log)
                || (!!log.requestedEdit && log.requestedEdit.state !== "Requested" && log.requestedEdit.state !== "Transitioning" && !!log["verifyDateTime"]);
        } else {
            logFilter = isDateExcluded;
        }

        filteredCollection = filteredCollection.filter(log => !logFilter(log));
        if (!forceRender && this.filteredCollection && this.app.dutyStatusLogService.areCollectionsEqual(this.filteredCollection, filteredCollection)) {
            // The logs on a different day could have changed, so we should rerender the navigation always
            if (!this.verifyOnly) {
                this.renderTemplate();
                this.navigationView?.render();
            }
            return CancelablePromise.resolve<void>();
        }

        let newCollection: IViewLog[] = filteredCollection.map(log => log.clone());

        // Historical logs are not part of the in-window logsCollection, so day-grouping AND
        // duration must be computed against the rendered collection itself. Otherwise
        // isLastLogOfDay would treat every historical log as the last of its day (indexOf returns
        // -1 in logsCollection) — splitting the day and dropping the continuation placeholder —
        // and getDuration would find no "next log", making durations fall through to now.
        let groupingCollection: DutyStatusLog[] = (isHistoricalDay && this.historicalLogs !== null) ? newCollection : this.logsCollection;

        let getMinuteDuration = (log: DutyStatusLog): number => this.app.dutyStatusLogService.getDuration(groupingCollection, log) / (1000 * 60);

        let dailyMetres = 0;
        let totalOnDutyHours = 0;
        let totalIgnoredLogMinutes = 0;
        let ignoredLogCount = 0;
        let dailyLogs: IViewLog[] = [];
        let dailyLogViews: IDailyViewDictionary = {};
        let isInCanadaWithPC = false;

        // First load existing addresses, get the missing ones later to not hold up the page load
        let missingAddresses = this.getMissingAddresses(newCollection);
        this.populateAddressCache(missingAddresses, populateDownloadedAddressesRedux(this.app.store, missingAddresses), newCollection);

        this.populateLogsPromise = CancelablePromise.all(newCollection.map(log => this.app.dutyStatusLogService.getAnnotations(log, this.translator)))
            .then(anns => {
                const linkedAnnotations = indexLinkedExemptionAnnotations(this.annotations);
                newCollection.forEach((log, index) => {
                    let logDate = log.getDate();
                    let localizedDate = dayjs(dstCalculator.toLocalDateTime(logDate));

                    let minutesDifference = getMinuteDuration(log);
                    if (ON_DUTY_LOGS.indexOf(log.status) > -1) {
                        totalOnDutyHours += (minutesDifference / 60 || 0);
                    }
                    if (log.isIgnored) {
                        totalIgnoredLogMinutes += (minutesDifference || 0);
                        ignoredLogCount += 1;
                    }

                    [isInCanadaWithPC, dailyMetres] = addLogDistance(log, dailyMetres, this.hosUtils, isInCanadaWithPC);

                    const logAnnotations = [...anns[index], ...getLinkedExemptionAnnotations(log, linkedAnnotations, anns[index])] as (IAnnotation | INewAnnotation)[];
                    let annotations = logAnnotations.reduce((ann: string[], annotation) => {
                        let id: string | undefined = (annotation as IIdEntity).id;
                        let populatedAnnotation: IAnnotation | INewAnnotation = (id && this.annotations[id]) || annotation;
                        populatedAnnotation.hasOwnProperty("comment") && ann.push(formatExemptionEditAnnotation(
                            populatedAnnotation["comment"],
                            timestamp => dayjs(dstCalculator.toLocalDateTime(new Date(timestamp))).format("L LT")
                        ));
                        return ann;
                    }, []);

                    log._date = localizedDate.format("LT"); // Time only for papercut fixes ("LT")
                    log._odometer = log.isEwdLog() && typeof log.odometer !== "undefined" ? this.app.localizerService.toLocalDistanceString(log.odometer) : "";
                    log._isEWD = this.isEWD;
                    log._isLoginLogoffLog = log.isLoginLogoffLog();
                    log._duration = convertMinutesToTimeString(minutesDifference);
                    log._annotations = annotations;
                    log._isControlLog = log.isControlLog();
                    log._controlLogBadge = log.getControlLogBadge();
                    log._hasAddress = log.isValidLocation();
                    log._isPlaceholder = log.isPlaceholder();
                    log._isRequestedEdit = this.app.dutyStatusLogService.isPlaceholderRequestedEdit(this.logsCollection, log)
                        || log.hasPendingRequest() || this.requestedAddShortenYMPCLogIdSet.has(log.id);
                    log._isVerified = log.isVerified();
                    log._isTransitioning = log.isAwaitingTransition();
                     // hos.ts will not look up address if set to '...'
                    log._address = (log.state === "Requested" || log.state === "Transitioning")
                        ? "..." : (!log.location?.address?.formattedAddress && log._address) || (log.location?.address?.formattedAddress) || "";
                    log._violations = [];
                    log.className = `log ${ log._isRequestedEdit ? "hosRequestedEdit" : "" } ${ log._isVerified ? "_verified" : "" }`;
                    log.hosAddressHtml = this.hosAddress.getHtmlTemplate({
                        id: log.id,
                        address: log._address,
                        hasAddress: log._hasAddress,
                        isRequestedEdit: log._isRequestedEdit
                    });

                    if (log.status === "PC_Exempted") {
                        log._customExemptHOSAbbreviation = getCustomExemptHOSAbbreviation(this.app.store.getState().systemSettings);
                    }

                    // Set translated status for all logs
                    log._translatedStatus = getTranslatedStatus(log.status, log._customExemptHOSAbbreviation || "", this.getString.bind(this));

                    dailyLogs.push(log);

                    if (this.app.dutyStatusLogService.isLastLogOfDay(groupingCollection, log)) {
                        let isRequestedLogsDayInternal = dailyLogs.some(l => l._isRequestedEdit && !l.placeholder);
                        let verifyLabel = isRequestedLogsDayInternal ? this.getString("Accept and certify") : this.getString("Certify");
                        dailyLogViews[getStartOfDayUserTimezone(logDate).toISOString()] = {
                            dailyHeaderData: {
                                dateTime: logDate,
                                mileageDate: localizedDate.format("ll"),
                                mileageTotal: this.app.localizerService.toLocalDistanceString(dailyMetres),
                                onDutyTime: convertDecimalToTimeString(totalOnDutyHours, true)
                                    .replace("{0}", this.getString("h"))
                                    .replace("{1}", this.getString("{minutes}m").replace("{minutes}", "")),
                                isAllDayVerified: !unverifiedDays[getDateTokenString(logDate, true)],
                                isRequestedLogsDay: isRequestedLogsDayInternal,
                                verifyOnly: this.verifyOnly,
                                dailyViolations: [],
                                isEditedPlaceholderDay: newCollection.some(l => l._isPlaceholder && l._isRequestedEdit),
                                isInCanadaWithPC: isInCanadaWithPC,
                                isEWD: this.app.store.getState().data.ELDJurisdiction === "EWD",
                                ignoredLogMessage: ignoredLogCount > 0 ? this.getString("Excludes {0} ignored logs ({time}) which will still appear on Roadside Checks.")
                                    .replace("{0}", ignoredLogCount.toString())
                                    .replace("{time}", convertMinutesToTimeString(totalIgnoredLogMinutes)) : "",
                                verifyLabel: `${ verifyLabel }`,
                                verifyDateLabel: `${ verifyLabel } ${ dayjs(dstCalculator.toLocalDateTime(logDate), undefined).format("ddd MMM D") }`
                            },
                            dailyLogs
                        };
                        dailyLogs = [];
                        dailyMetres = 0;
                    }
                });

                this.populateViolations(dailyLogViews);
                const hasRequestedPcYmEdits = newCollection.some(log => this.requestedAddShortenYMPCLogIdSet.has(log.id));
                this.model.set({
                    hasRequestedEdits: (this.verifyOnly ? this.app.dutyStatusLogService.hasRequestedEdits(newCollection) : false) || hasRequestedPcYmEdits,
                    hasLogsToVerify: (this.verifyOnly ? this.app.dutyStatusLogService.getNotVerifiedLogs(newCollection).length > 0 : false) || hasRequestedPcYmEdits,
                    hasLogs: newCollection.length > 0
                }, { silent: true });

                this.filteredCollection = filteredCollection;
                this.dailyLogViews = dailyLogViews;
                return this.renderTemplate();
            })
            .then(() => this.fetchMissingLocations(newCollection));

        return this.populateLogsPromise;
    };

    private setLastClickedLog(payload?: IViewedLog) {
        this.app.store.dispatch(MobileActions.setLastViewedLog(payload));
        this.clickedLog = payload;
    }

    private clearClickedLog() {
        this.app.store.dispatch(MobileActions.setLastViewedLog(undefined));
    }

    private scrollToClickedElement() {
        if (!this.clickedLog || this.verifyOnly) {
            return;
        }

        if (this.clickedLog.id) {
            let logElement = document.getElementById(`checklist__${ this.clickedLog.id }`);
            logElement && logElement.scrollIntoView();
        }
        this.clickedLog = undefined;
    }

    private getTemplateObject() {
        return {
            hasLogs: this.model.get("hasLogs"),
            hasLogsToVerify: this.model.get("hasLogsToVerify"),
            hasRequestedEdits: this.model.get("hasRequestedEdits"),
            workflow: this.verifyOnly,
            odometers: this.verifyOnly ? [] : getOdometer(this.currentDay, this.app.localizerService, this.app.store, this.app.userService.getCurrentUser()?.id, this.getHistoricalOdometerSource()),
            isEWD: this.isEWD,
            hasMalfunctions: this.checkForMalfunctions(),
            isHistoricalLoading: this.isHistoricalLoading,
            historicalLoadError: this.historicalLoadError
        };
    }

    private clickLog(log: Backbone.Model) {
        if (log.attributes._awaitingTransition) {
            return;
        }
        this.setLastClickedLog({
            id: log.get("id"),
            date: getStartOfDayUserTimezone(new Date(log.attributes.dateTime)).toISOString()
        });

        let viewLogId = log.get("placeholder") ? log.get("parentId") : log.get("id");
        let isRequestedEdit = !!log.attributes.requestedEdit || log.attributes.state === "Requested" || this.requestedAddShortenYMPCLogIdSet.has(viewLogId);

        if (log && (!this.verifyOnly || isRequestedEdit)) {
            let filteredViolations = this.violations
                .filter(vio => vio.logId === viewLogId)
                .map((vio: any) => ({ start: new Date(vio.fromDate).toISOString(), reason: populateViolationReason(vio) }));

            if (this.clickLogAction) {
                this.clickLogAction(viewLogId, filteredViolations);
            } else {
                this.app.router.navigate(`hos/tabs/logs/view,${ rison.encode({ id: viewLogId, violations: filteredViolations }) }`, true);
            }
        }
    }

    private generateLogCollectionListImpl(): CancelablePromise<void> {
        if (!(this.filteredCollection && this.filteredCollection.length)) {
            return CancelablePromise.resolve();
        }

        let clickLogHandler = this.clickLog.bind(this);
        let fragment = document.createDocumentFragment();

        let daysToShow = Object.keys(this.dailyLogViews);
        let displayPromise: CancelablePromise<HTMLDivElement>[] = [];
        let logListViews: ListView[] = [];

        for (let i = daysToShow.length - 1; i >= 0; i--) {
            let day = daysToShow[i];
            let dailyData = this.dailyLogViews[day];

            let dailyHeaderData = dailyData.dailyHeaderData;
            let dailyLogs = dailyData.dailyLogs.map(log => {
                if (log.isPlaceholder()) {
                    log.id = `${ log.parentId }_placeholder_${ day.substring(0, 10) }`;
                }
                return log;
            });
            let headerElement = createElement("div");

            displayPromise.push(this.translator.translateTemplate(this.templateDailyHeader(dailyHeaderData))
                .then(tmp => {
                    let tempEl = document.createDocumentFragment();
                    [].forEach.call(tmp, item => {
                        tempEl.appendChild(item);
                    });
                    headerElement.appendChild(tempEl);
                    let listElement = createElement("div", "logs-list");
                    let logListView = generateChecklist({
                        el: listElement,
                        items: dailyLogs,
                        labelTemplate: logTemplate,
                        isCheckable: false,
                        selectCallback: (item, event) => {
                            !event.target || !event.target.classList.contains("addressButton") && clickLogHandler(item);
                        },
                        context: this,
                        translator: this.translator,
                        reverseItems: true
                    });

                    logListViews.push(logListView);
                    headerElement.appendChild(listElement);
                    return headerElement;
                }));
        }

        return CancelablePromise.all(displayPromise)
            .then((headerElements) => {
                for (let i = 0; i < headerElements.length; i++) {
                    fragment.appendChild(headerElements[i]);
                }

                this.removeListViews();
                this.logListViews = logListViews;
                let logsList = document.getElementById("logs-list-container");
                this.spinner.stop();
                if (logsList) {
                    logsList.innerHTML = "";
                    logsList.appendChild(fragment);
                }
                return;
            });
    }

    private generateLogsCollectionList(): CancelablePromise<void> {
        clearTimeout(this.renderChecklistTimeout);
        this.spinner.stop();

        return new CancelablePromise(resolve => {
            this.spinner.spin(document.getElementById("logs-list-container"));
            this.renderChecklistTimeout = setTimeout(() => {
                void this.generateLogCollectionListImpl().then(resolve);
            }, 1);
        });
    }

    private renderTemplateVerify() {
        return this.display(this.templateLogs(this.getTemplateObject()))
            .then(() => this.generateLogsCollectionList());
    }

    private renderTemplateView() {
        let buttonContainer;
        let odometerContainer: HTMLElement | null;
        return this.display(this.templateLogs(this.getTemplateObject()))
            .then(() => {
                if (this.isHistoricalLoading && this.shouldUseHistoricalLoading(this.currentDay)) {
                    const spinnerContainer = document.querySelector("#hos-historical-loading .spinner-container") as HTMLElement;
                    spinnerContainer && this.bigSpinner.spin(spinnerContainer);
                    // Keep the date navigation visible alongside the spinner so the user can
                    // continue navigating while the historical day loads.
                    this.renderDateSelector();
                    return CancelablePromise.resolve<void>();
                }

                this.bigSpinner.stop();
                this.app.setFullWidth(true);
                buttonContainer = document.getElementById("logs-buttons-container") as HTMLInputElement;
                buttonContainer && hide(buttonContainer);
                odometerContainer = document.getElementById("graphOdometers");
                odometerContainer && hide(odometerContainer);

                return this.generateLogCollectionListImpl();
            })
            .then(() => {
                if (this.isHistoricalLoading && this.shouldUseHistoricalLoading(this.currentDay)) {
                    return;
                }

                this.renderDateSelector();
                this.graphView && this.graphView.setElement(document.getElementById("graph-inner-container")).render();
                this.menu && this.menu.render();

                buttonContainer && show(buttonContainer);
                if (odometerContainer) {
                    let odometerData = this.verifyOnly ? [] : getOdometer(
                        this.currentDay,
                        this.app.localizerService,
                        this.app.store,
                        this.app.userService.getCurrentUser()?.id,
                        this.getHistoricalOdometerSource()
                    );

                    if (odometerData.length > 0) {
                        let odometerItems = odometerData.map(item => new ExpandableItem({ id: item.id, title: item.deviceName, content: this.odometerItemTemplate(item) }));
                        let odometersList = new ExpandableList({app: this.app, items: odometerItems });
                        odometersList.setElement(odometerContainer.querySelector(".panel__content"));
                        odometersList.render();
                        show(odometerContainer);
                    }
                }

                this.scrollToClickedElement();
            });
    }

    private renderDateSelector() {
        const container = document.getElementById("datepicker-container");
        if (!container) {
            return;
        }

        this.navigationView?.setElement(container).render();

        const reactContainer = document.getElementById("react-datepicker-container");
        if (!reactContainer) {
            return;
        }

        const nightMode = this.app.store.getState().data.nightMode;
        const language = (this.app.translationService.getLanguage() || "en") as TSupportedLanguage;

        // Unrestricted backward navigation for users with log history
        const minDate = this.logsCollection?.length > 0 ? undefined : getStartOfDayUserTimezone(getServerDate());

        if (this.dateSelectorReactRoot && reactContainer !== this.dateSelectorContainer) {
            this.dateSelectorReactRoot.unmount();
            this.dateSelectorReactRoot = null;
        }

        if (!this.dateSelectorReactRoot) {
            this.dateSelectorReactRoot = createRoot(reactContainer);
            this.dateSelectorContainer = reactContainer;
        }

        this.dateSelectorReactRoot.render(
            React.createElement(
                LanguageProvider,
                { language },
                React.createElement(
                    ThemeDrive,
                    { dark: nightMode },
                    React.createElement(
                        UserFormatProvider,
                        {
                            toLocalDateTime: dstCalculator.toLocalDateTime,
                            toReverseOffsetDateTime: (date: Date | string) =>
                                dstCalculator.toReverseOffsetDateTime(typeof date === "string" ? new Date(date) : date)
                        },
                        React.createElement(DateSelector, {
                            key: this.currentDay.toISOString(),
                            callback: this.navigateToDate,
                            minDate,
                            maxDate: getStartOfDayUserTimezone(getServerDate()),
                            initialDate: this.currentDay
                        })
                    )
                )
            )
        );
    }

    private removeListViews() {
        this.logListViews.forEach(listView => {
            listView && listView.remove();
        });
    }

    private renderTemplate() {
        return this.verifyOnly ? this.renderTemplateVerify() : this.renderTemplateView();
    }

    private formatAddress(addressObject: IAddress) {
        return addressObject.formattedHosAddress || addressObject.formattedAddress ? addressObject.formattedHosAddress || addressObject.formattedAddress : "";
    }

    private setLogAddress(log: IViewLog, logAddress) {
        let location = log.location || {} as IAddressLookupData;
        log.location = { address: logAddress, ...location };
        log._address = this.formatAddress(logAddress);
        log.hosAddressHtml = this.hosAddress.getHtmlTemplate({
            id: log.id,
            address: log._address,
            hasAddress: log._hasAddress || false,
            isRequestedEdit: log._isRequestedEdit || false
        });
    }

    private getMissingAddresses(collection: DutyStatusLog[]) {
        let requestLocations: IAddressSearch[] = [];

        collection.forEach(log => {
            let logId = log.id;
            if (!log._isTempIoxLog && log.location && log.location.location
            && log.isValidLocation() && !this.logsAddressCache.hasOwnProperty(logId)
            && (!log.location.address || !log.location.address.formattedAddress)) {
                requestLocations.push({
                    "id": logId,
                    "coordinate": log.location.location
                });
            }
        });
        return requestLocations;
    }

    private populateAddressCache(requestLocations: IAddressSearch[], addresses: IAddress[], collection: DutyStatusLog[]) {
        if (addresses && addresses.length) {
            logger.log(logger.Source.UI, "Got new addresses for HOS logs, re-rendering HOS logs list", this.app.userService.getCurrentUser());
            let addressCache = addresses.reduce((acc, addressEntity: IAddress, index) => {
                addressEntity && addressEntity.formattedHosAddress && (acc[requestLocations[index].id] = addressEntity);
                return acc;
            }, <ICachedAddress>{});

            this.logsAddressCache = extend(addressCache, (this.logsAddressCache || {}));
        }

        this.populateAddresses(collection);
    }

    private populateAddresses(collection: DutyStatusLog[]) {
        collection.forEach(log => {
            let logId = log.id;
            if (logId) {
                if (log.location?.address?.formattedAddress) {
                    this.setLogAddress(log, log.location.address);
                } else if (this.logsAddressCache.hasOwnProperty(logId) && (this.logsAddressCache[logId].formattedHosAddress)) {
                    // Stitch together logs and their cached addresses
                    let address = this.logsAddressCache[logId];
                    address && this.setLogAddress(log, address);
                }
            }
        });
    }

    private fetchMissingLocations(collection: DutyStatusLog[]): CancelablePromise<void> {
        let requestLocations = this.getMissingAddresses(collection);
        if (requestLocations.length > 0) {
            return fetchAddresses(this.api, this.app.store, requestLocations, false)
                .then((addresses: IAddress[]) => {
                    this.populateAddressCache(requestLocations, addresses, collection);
                    return this.renderTemplate();
                })
                .catch(e => logger.error(logger.Source.ERROR, `Can't get locations. Error: ${ e ? e.message : "" }`, this.app.userService.getCurrentUser(), e));
        }
        return CancelablePromise.resolve();
    }

    private manualInputAddress(e) {
        let id = e.target.getAttribute("id").replace("addressButton__", "");
        let log = this.app.dutyStatusLogService.getLogById(this.filteredCollection, id);
        let currentUser = this.app.userService.getCurrentUser();
        let updateNavigationView = () => {
            if (!this.app.dutyStatusLogService.isMissingAddressDay(this.filteredCollection)) {
                this.navigationView?.updateIssue(this.currentDay.toISOString(), false, false, true);
            }
        };
        this.hosAddress.handleAddressButtonClick(log, currentUser, this.isEWD, this.app.store.getState().data.nightMode, this.populateLogs, updateNavigationView);
    }

    private acceptEdits(unverifiedLogsForPeriod: DutyStatusLog[], allSortedHosLogs: DutyStatusLog[], pcYmEditIdsToResolve: string[]): CancelablePromise<DutyStatusLog[]> {
        return new CancelablePromise(resolve => {
            const currentUser = this.app.userService.getCurrentUser();
            let logsToAccept = this.app.dutyStatusLogService.getRequestedEdits(unverifiedLogsForPeriod);
            const newlyAddedSDCLogs: DutyStatusLog[] = [];
            const requiredLogsForDifferentDays: DutyStatusLog[] = [];
            const exemptionEditPromises: CancelablePromise[] = [];
            if (logsToAccept.length > 0) {
                logsToAccept.map((log: DutyStatusLog) => {
                    if (log.requestedEdit) {
                        log.requestedEdit.coDrivers = log.coDrivers;
                        setCoDriversForCarrierDriverRequest(true, log.requestedEdit as IDutyStatusLog, log as IDutyStatusLog);
                    }
                    this.app.dutyStatusLogService.saveRequest(log, true);
                    const skipSdc = !log.isStandardLog() || log.isAutomaticLog();
                    const newLogs = this.app.dutyStatusLogService.getAdditionalLogsBeforeNewLogIfDrivingInExemption(new Date(log.dateTime), currentUser as IUser, skipSdc);
                    newLogs.length && newlyAddedSDCLogs.push(...newLogs);
                });

                let firstLog = logsToAccept[0];
                let lastLog = logsToAccept[logsToAccept.length - 1];
                let isStatusAllowed = this.app.dutyStatusLogService.isAllowedToBePaired(firstLog);
                this.addPairedLogsIfRequired(requiredLogsForDifferentDays, isStatusAllowed, firstLog, allSortedHosLogs);
                this.addPairedLogsIfRequired(requiredLogsForDifferentDays, lastLog.isAutomaticDriving(), lastLog, allSortedHosLogs);
            }

            const additionalLogsAccepted: DutyStatusLog[] = [];
            if (pcYmEditIdsToResolve.length > 0) {
                let exemptionLogEditGroups = this.app.dutyStatusLogService.getGroupedExemptionLogEdits(undefined, pcYmEditIdsToResolve);
                exemptionLogEditGroups.forEach(group => {
                    if (group.editedExemptionLog) {
                        additionalLogsAccepted.push(group.editedExemptionLog);
                        this.app.dutyStatusLogService.saveRequest(group.editedExemptionLog, true);
                        exemptionEditPromises.push(this.sync.dutyStatusLogs.setItem(group.editedExemptionLog, true, false));

                        group.logsToEdit.forEach(log => {
                            this.app.dutyStatusLogService.saveRequest(log, true);
                            log.verify();
                            exemptionEditPromises.push(this.sync.dutyStatusLogs.setItem(log, true, false));
                        });

                        group.logsToRemove.forEach(log => {
                            exemptionEditPromises.push(this.sync.dutyStatusLogs.removeItem(log, true));
                        });
                    }
                });
            }

            return (newlyAddedSDCLogs.length ? CancelablePromise.all(newlyAddedSDCLogs.map(log => this.sync.dutyStatusLogs.addItem(log, true))) : CancelablePromise.resolve())
                .then(() => CancelablePromise.all(exemptionEditPromises))
                .then(() => {
                    if (requiredLogsForDifferentDays.length > 0) {
                        return this.addLogsToSyncLater(requiredLogsForDifferentDays, false).then(() => resolve(additionalLogsAccepted));
                    }
                    resolve(additionalLogsAccepted);
                    return;
                });
        });
    }

    private addPairedLogsIfRequired(logsToSet: DutyStatusLog[], correctStatus: boolean, boundaryLog: DutyStatusLog, allSortedHosLogs?: DutyStatusLog[]) {
        if (correctStatus && allSortedHosLogs) {
            let pairedLog = this.app.dutyStatusLogService.findPairedLog(boundaryLog, allSortedHosLogs);
            if (pairedLog) {
                this.app.dutyStatusLogService.saveRequest(pairedLog, true);
                logsToSet.push(pairedLog);
            }
        }
    }

    private populateCertifiedLogDays(logs: DutyStatusLog[]): string[] {
        let certifiedLogDays: string[] = [];
        let certifiedLogDaysHash: { [date: string]: string } = {};

        logs.forEach((log) => {
            let localDateTime = dstCalculator.toLocalDateTime(log.getDate());
            let isoDate = `${ formatUtcDateString(localDateTime) }T00:00:00.000Z`;
            certifiedLogDaysHash[getDateTokenString(localDateTime)] = isoDate;
        });

        for (let dateHash in certifiedLogDaysHash) {
            if (certifiedLogDaysHash.hasOwnProperty(dateHash)) {
                certifiedLogDays.push(certifiedLogDaysHash[dateHash]);
            }
        }

        return certifiedLogDays;
    }

    private createCertifyRecords(logs: DutyStatusLog[], currentUser: DeepReadonly<IUser>) {
        let logDays = this.populateCertifiedLogDays(logs);
        let currentVehicle = getCurrentVehicle();
        let now = getServerDate().toISOString();
        let coDrivers = this.app.userService.getCoDrivers(currentUser.name);

        // Add logging to confirm all logs are verified and created as expected
        let logsToVerify =  logs.filter(log => !log.placeholder);
        logger.log(logger.Source.USER_EVENT, `Will certify ${ logsToVerify.length } logs and will create ${ logDays.length } certify records for the current user`, currentUser);

        return logDays.length ? CancelablePromise.all(logDays.map(date => {
            let newLog = new DutyStatusLog({
                id: idSerializer.get(),
                dateTime: now,
                verifyDateTime: date,
                device: { id: currentVehicle ? currentVehicle.id : "NoDeviceId" },
                driver: { id: currentUser.id },
                coDrivers,
                status: "Certify",
                origin: "Manual"
            });

            return this.sync.dutyStatusLogs.addItem(newLog, true);
        })) : CancelablePromise.resolve();
    }

    private addLogsToSyncLater(logs: DutyStatusLog[], shouldVerify = true) {
        return logs.length ? CancelablePromise.all(logs.map((log: DutyStatusLog) => {
            if (shouldVerify) {
                log.verify();
                log.requestedEdit && log.requestedEdit.verify();
            }
            return this.sync.dutyStatusLogs.setItem(log, true, !(log.requestedAdd || log.requestedEdit));
        })) : CancelablePromise.resolve();
    }

    private verifyLogs(logsPeriod?: IReportPeriod, acceptEdits = false) {
        let totalNotVerifiedLogs: DutyStatusLog[] = this.app.dutyStatusLogService.getNotVerifiedLogs(this.filteredCollection);

        let logsToVerify: DutyStatusLog[] = logsPeriod ? this.app.dutyStatusLogService.getLogsByDate(totalNotVerifiedLogs, logsPeriod) : totalNotVerifiedLogs;

        let pcYmEditIdsToResolve: string[] = [];
        const logsToCheck = logsPeriod
            ? this.app.dutyStatusLogService.getLogsByDate(this.filteredCollection, logsPeriod)
            : this.filteredCollection;
        pcYmEditIdsToResolve = logsToCheck.filter(log => this.requestedAddShortenYMPCLogIdSet.has(log.id)).map(log => log.id);

        const hasLogsToVerify = !(logsToVerify.length === 0 && pcYmEditIdsToResolve.length === 0);
        if (!hasLogsToVerify) {
            return;
        }

        const countOfLogsToVerify = logsToVerify.length;
        if (pcYmEditIdsToResolve.length > 0) {
            // Remove isRequestedRemoval PC/YM edits from logsToVerify as they will be handled separately
            logsToVerify = logsToVerify.filter(log => !this.requestedAddShortenYMPCLogIdSet.has(log.id) || !log.requestedEdit?.isRequestedRemoval);
        }

        let verifyConfirmDialog: Dialog;
        let verifyLogsImpl = (additionalLogsAccepted: DutyStatusLog[]) => {
            let currentUser = this.app.userService.getCurrentUser();
            if (hasLogsToVerify && currentUser) {
                // Create certify logs for the range of certified days
                return CancelablePromise.all([this.createCertifyRecords((logsToVerify.concat(additionalLogsAccepted)), currentUser), this.addLogsToSyncLater(logsToVerify)])
                    .then(() => {
                        // Only perform once sync after all logs verified
                        !this.loggingOut && this.sync.synchronize(false, null, [SyncType.HOS]);
                        verifyConfirmDialog.remove();
                        if (this.verifyOnly && totalNotVerifiedLogs.length <= countOfLogsToVerify) {
                            this.proceedActionIfNecessary(false);
                        } else {
                            let renderReady: Promise<void> = Promise.resolve();
                            if (this.shouldUseHistoricalLoading(this.currentDay)) {
                                const userId = this.api.getUser()?.id;
                                const dateKey = this.makeDateKey(this.currentDay);
                                if (userId) {
                                    const existing = this.app.store.getState().historicalDayTracker[userId]?.[dateKey];
                                    // logsToVerify still has verifyDateTime set from addLogsToSyncLater
                                    // — merge them into the tracker so cache hits return certified logs
                                    const verifiedById = new Map(logsToVerify.map(l => [l.id, l]));
                                    const updatedHosLogs: DutyStatusLog[] = (existing?.hosLogs ?? []).map(raw => {
                                        const verified = verifiedById.get(raw.id);
                                        return verified ? new DutyStatusLog(verified) : new DutyStatusLog(raw);
                                    });
                                    const systemLogs = (existing?.systemLogs ?? []).map(raw => new DutyStatusLog(raw));
                                    // Accepted edits can change a log's date/time, vehicle, or status so
                                    // re-fetch odometer from the accepted logs. Certifying only sets
                                    // verifyDateTime so carry the existing fragment forward instead.
                                    const odometerPromise = acceptEdits
                                        ? this.historicalDayLoader.fetchDayOdometer(updatedHosLogs, systemLogs)
                                        : Promise.resolve(extend(true, {}, existing?.odometer ?? {}) as IDailyOdometer);
                                    renderReady = odometerPromise.then(odometer => {
                                        this.app.store.dispatch(HistoricalDayActions.trackHistoricalDay({
                                            userId,
                                            dateKey,
                                            loadedAt: existing?.loadedAt ?? Date.now(),
                                            hosLogs: updatedHosLogs,
                                            systemLogs,
                                            violations: extend(true, [], existing?.violations ?? []) as IViolation[],
                                            odometer
                                        }));
                                        this.historicalLogs = updatedHosLogs.filter(l => l.isRegularDisplayLog());
                                        this.historicalOdometer = odometer;
                                    });

                                    // Accepting a requested edit can change the status that continues
                                    // into later days. Drop every other cached day for this user so the
                                    // boundary/continuation placeholder is re-fetched (and shows the
                                    // accepted value) the next time those days are viewed.
                                    if (acceptEdits) {
                                        this.invalidateOtherCachedDays(userId, dateKey);
                                    }
                                }
                            }
                            this.navigationView?.updateIssue(this.currentDay.toISOString(), true, acceptEdits);
                            void renderReady.then(() => {
                                this.loadLogs();
                                void this.populateLogs(true);
                            });
                        }
                    });
            }
            return CancelablePromise.resolve();
        };

        let templateStr = _.template(certifyDialogTemplate)({
            isCanada: isInCanada(this.app.hosUtils.getLatestHosRuleSetIdShortened()),
            hasRequestedPcYmEdits: pcYmEditIdsToResolve.length > 0
        });

        verifyConfirmDialog = new Dialog(
            null,
            templateStr,
            this.translator,
            "Certify",
            [{
                id: "cancel",
                text: "Not ready",
                className: "button_secondary",
                callback: () => {
                    verifyConfirmDialog.remove();
                }
            }, {
                id: "verifyLogOk",
                spinner: true,
                text: "Agree",
                callback: () => {
                    if (acceptEdits) {
                        void this.acceptEdits(logsToVerify, this.logsCollection, pcYmEditIdsToResolve).then(additionalLogsAccepted => verifyLogsImpl(additionalLogsAccepted));
                    } else {
                        void verifyLogsImpl([]);
                    }
                }
            }],
            {
                closable: false,
                isNightMode: this.app.store.getState().data.nightMode
            }
        );
        void verifyConfirmDialog.open();
    }

    private verifyAndAccept(e, acceptEdits: boolean) {
        let date: string | undefined = e && e.currentTarget && e.currentTarget.getAttribute("data-date");
        date && this.verifyLogs(getLocalDaySpan(new Date(date)), acceptEdits);
    }

    private loadFilteredMalfunctionLogs() {
        let currentVehicle = getCurrentVehicle();
        if (!currentVehicle) {
            return [];
        }
        let filterStartDateMs = this.hosUtils.getUnidentifiedDrivingRetentionStartDate(this.app.store.getState().data.ELDJurisdiction === "CANELD").getTime();
        const isCurrentDeviceLog = (log: DutyStatusLog) => {
            return log.getDeviceId() === currentVehicle?.id;
        };
        const isOldUnidentifiedDrivingComplianceLog = (log: DutyStatusLog) => {
            return log.isUnidentifiedDrivingComplianceLog() && log.getDate().getTime() < filterStartDateMs;
        };
        return this.app.dutyStatusLogService.getHOSLogs(true, undefined, false, true, true)
            .filter(
                log =>
                    isCurrentDeviceLog(log)
                    && !log.isPositionComplianceLog()
                    && !isOldUnidentifiedDrivingComplianceLog(log)
                    && log.isMalfunctionLog()
            );
    }

    private checkForMalfunctions(): boolean {
        const recentMalfunctionLogs = this.loadFilteredMalfunctionLogs();
        return recentMalfunctionLogs.length > 0;
    }


    private verify(e) {
        this.verifyAndAccept(e, false);
    }

    private verifyAll() {
        this.verifyLogs();
    }

    private verifyAndAcceptDay(e) {
        this.verifyAndAccept(e, true);
    }

    private acceptAndVerifyAll() {
        this.verifyLogs(undefined, true);
    }

    private proceedWithAudit() {
        let userId = this.app.api.getUser()?.id;
        userId && this.app.store.dispatch(OpenCabWorkflowActions.setUserSkippedUnverifiedLogsDate(userId, getServerDate().toISOString()));
        this.proceedActionIfNecessary(true);
    }

    private proceedActionIfNecessary(needsAudit: boolean) {
        needsAudit && auditSkip(this.app.api, AuditType.HosVerify, this.loggingOut ? "Logout" : "Login");
        dispatcher.trigger(DispatcherEvents.CompleteWorkflowStep);
    }

    private saveCurrentDay() {
        this.setLastClickedLog({ date: this.currentDay.toISOString() });
    }

    private addLog() {
        this.saveCurrentDay();
        this.app.router.navigate(`hos/tabs/logs/addLog,${ rison.encode({ dateTime: this.currentDay.toISOString() }) }`, true);
    }

    private seeMalfunctions() {
        this.saveCurrentDay();
        this.app.router.navigate("hos/malfunctions", true);
    }

    private goToRealLog(e) {
        let date = e.currentTarget.getAttribute("data-date");
        let totalNotVerifiedLogs = this.app.dutyStatusLogService.getNotVerifiedLogs(this.filteredCollection);
        let daySpan = getLocalDaySpan(date);
        let logs = this.app.dutyStatusLogService.getLogsByDate(totalNotVerifiedLogs, daySpan);
        let placeholderLog = logs.filter(o => o.isPlaceholder())[0];
        let realLogId = placeholderLog.parentId;

        let filteredViolations = this.violations
            .filter(vio => vio.logId === realLogId)
            .map((vio: any) => ({ start: new Date(vio.fromDate).toISOString(), reason: populateViolationReason(vio) }));

        let goToRealLogDialog = new Dialog(
            null,
            "Please review the requested edit before certifying the log",
            this.translator,
            "Certify",
            [{
                id: "cancel",
                text: "Cancel",
                className: "button_secondary",
                callback: () => {
                    goToRealLogDialog.remove();
                }
            },
            {
                id: "review",
                text: "Review",
                callback: () => {
                    this.setLastClickedLog({
                        id: placeholderLog.id,
                        date: getStartOfDayUserTimezone(new Date(placeholderLog.dateTime)).toISOString()
                    });
                    goToRealLogDialog.remove();
                    if (this.clickLogAction) {
                        this.clickLogAction(realLogId, filteredViolations);
                    } else {
                        this.app.router.navigate(`hos/tabs/logs/view,${ rison.encode({ id: realLogId, violations: filteredViolations }) }`, true);
                    }
                }
            }],
            {
                closable: false,
                isNightMode: this.app.store.getState().data.nightMode
            }
        );
        void goToRealLogDialog.open();
    }
}